import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { useToast } from "@/features/toast";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, RefreshCw, ArrowLeft, CheckCircle } from "lucide-react";
import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import { AuthStage } from "@/components/auth/auth-stage";
import {
  authFooterClass,
  authLinkClass,
  authStackClass,
  authTitleClass,
} from "@/components/auth/auth-ui";
import { AuthSupportLink } from "@/components/auth/auth-support-link";
import { AuthIconBadge } from "@/components/auth/auth-icon-badge";

export default function PasswordResetSentPage() {
  const { t } = useTranslation();
  const [isResending, setIsResending] = useState(false);
  const { success, error } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // ForgotPasswordPage'den gelen email bilgisi
  const email = location.state?.email || "";

  // Email bilgisi yoksa forgot password sayfasına yönlendir
  useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
    }
  }, [email, navigate]);

  // Yeniden link gönder
  const handleResendLink = async () => {
    setIsResending(true);
    try {
      await apiClient.post(API_ENDPOINTS.forgotPassword, {
        email,
      });

      success(t("auth.checkYourEmailForReset"), {
        title: t("auth.resetLinkSent"),
      });
    } catch (errorCatch: any) {
      // Rate limiting hatası için özel mesaj
      if (
        errorCatch.response?.status === 429 ||
        errorCatch.response?.data?.code === "TOO_MANY_REQUESTS" ||
        errorCatch.response?.data?.message?.includes("rate limit")
      ) {
        error(t("auth.resendTooFrequent"), {
          title: t("auth.resendError"),
        });
      } else {
        error(
          errorCatch.response?.data?.message || t("error.somethingWentWrong"),
          {
            title: t("auth.resendError"),
          }
        );
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthStage>
          <div className={authStackClass}>
            {/* Header */}
            <div className="flex flex-col items-center gap-4">
              <AuthIconBadge variant="success">
                <CheckCircle className="h-6 w-6 text-text-success dark:text-text-success" />
              </AuthIconBadge>
              <div className="text-center space-y-2">
                <h1 className={authTitleClass}>
                  {t("auth.resetLinkSent")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("auth.resetLinkSentDescription", { email })}
                </p>
              </div>
            </div>

            {/* Info Section */}
            <div className="space-y-6">
              <div className="bg-muted/50 dark:bg-muted/30 p-4 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-primary dark:text-primary mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-text-primary dark:text-gray-100">
                      {t("auth.checkYourEmail")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("auth.resetLinkInstructions")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center space-y-4">
                <p className="text-xs text-muted-foreground">
                  {t("auth.didntReceiveEmail")}
                </p>
                <Button
                  variant="outline"
                  onClick={handleResendLink}
                  disabled={isResending}
                  className="w-full"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 text-text-secondary dark:text-gray-100 ${
                      isResending ? "animate-spin" : ""
                    }`}
                  />
                  <span className="flex items-center gap-2 text-text-secondary dark:text-gray-100">
                    {isResending
                      ? t("common.loading")
                      : t("auth.resendResetLink")}
                  </span>
                </Button>
              </div>
            </div>

            {/* Back to Login */}
            <div className="flex justify-center">
              <Link
                to="/login"
                className={`${authLinkClass} text-sm items-center gap-2`}
              >
                <ArrowLeft className="h-4 w-4" />
                {t("auth.backToLogin")}
              </Link>
            </div>

            {/* Destek çıkışı + Language/Theme Switcher */}
            <div className={authFooterClass}>
              <AuthSupportLink />
            </div>
          </div>
    </AuthStage>
  );
}
